import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, MessageSquare } from 'lucide-react';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';
import { CONTACT } from '../mock';
import Reveal from './Reveal';

const INFO = [
  { icon: Mail, label: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}`, color: '#0ef3fe' },
  { icon: Phone, label: 'Phone', value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, '')}`, color: '#088ffe' },
  { icon: MapPin, label: 'Location', value: CONTACT.location, color: '#5bd0ff' },
  { icon: Clock, label: 'Support', value: '24/7 Operations', color: '#0a6bd6' },
];

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact \u2014 Electronico Holdings';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white" data-testid="contact-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative pt-40 pb-20 px-5 grid-bg overflow-hidden">
          <div className="blob bg-[#0ef3fe] w-[380px] h-[380px] -top-20 -left-20 opacity-40" />
          <div className="blob bg-[#088ffe] w-[320px] h-[320px] top-10 right-0 opacity-40" />
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <Reveal>
              <span className="font-mono-accent text-xs text-[#0ef3fe]">// GET IN TOUCH</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
                Let's Build Something <span className="gradient-text">Together</span>
              </h1>
              <p className="text-white/60 mt-5 max-w-2xl mx-auto leading-relaxed">
                Whether you need electronic security, contract manufacturing or bold multimedia
                marketing, our team is ready to help. Reach out and we'll respond within one business day.
              </p>
            </Reveal>
          </div>
        </section>

        {/* INFO + MAP */}
        <section className="relative pb-24 px-5">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <div>
                <h2 className="font-display text-3xl font-bold mb-6">Contact Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {INFO.map((item) => {
                    const Icon = item.icon;
                    const inner = (
                      <>
                        <span className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}1a` }}>
                          <Icon className="h-5 w-5" style={{ color: item.color }} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-xs text-white/40">{item.label}</div>
                          <div className="font-medium break-words">{item.value}</div>
                        </div>
                      </>
                    );
                    return item.href ? (
                      <a key={item.label} href={item.href} data-testid={`contact-info-${item.label.toLowerCase()}`} className="flex items-center gap-4 neon-border rounded-2xl p-4 hover:border-white/25 transition-colors group">
                        {inner}
                      </a>
                    ) : (
                      <div key={item.label} data-testid={`contact-info-${item.label.toLowerCase()}`} className="flex items-center gap-4 neon-border rounded-2xl p-4">
                        {inner}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex items-center gap-3 text-sm text-white/50">
                  <MessageSquare className="h-4 w-4 text-[#0ef3fe]" />
                  Prefer to chat? Use Volt, our AI assistant, in the corner of any page.
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="neon-border rounded-3xl overflow-hidden" data-testid="contact-map">
                <iframe
                  title="Electronico Holdings location"
                  src="https://www.google.com/maps?q=Hermanus%2C%20Western%20Cape%2C%20South%20Africa&output=embed"
                  className="w-full h-[460px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* FOOTER */}
      </main>
      <SiteFooter />
    </div>
  );
}
